import {
  Checkbox,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { makeStyles } from 'tss-react/mui';
import { Alert } from '@mui/material';
import showNotification from 'commons/helpers/showNotification';
import maskUtils from 'commons/utils/maskUtils';
import { FontAwesomeIconStatus } from 'components/icon/FontAwesomeIconStatus';
import ConfirmationDialog from 'components/modals/ConfirmationDialog';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CustomerService from 'services/CustomerService';
import UnifyCustomerAutomaticToolbar from './UnifyCustomerAutomaticToolbar';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  row: {
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  paper: { padding: theme.spacing(2) },
  paperSaving: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  btnBack: { marginRight: 4 },
  labelNoResults: { marginTop: '10px', marginBottom: '10px' },
  cellCheckbox: { width: '40px' },
}));

const UNIFY_CUSTOMER_TYPE_PHONE = 'PHONE';

// COMPONENT
function UnifyCustomerAutomaticPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [contactList, setContactList] = useState([]);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [allBeingSelected, setAllBeingSelected] = useState(false);
  const [isModalConfirmOpen, setIsModalConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { classes } = useStyles();
  const navigate = useNavigate();

  const handleSelect = (contact) => {
    const isSelected = selectedIndexes.includes(contact);
    if (isSelected) {
      setSelectedIndexes((prevSelected) => prevSelected.filter((obj) => obj !== contact));
    } else {
      setSelectedIndexes((prevSelected) => prevSelected.concat([contact]));
    }
  };

  useEffect(() => {
    setIsLoading(true);
    CustomerService.findAutomaticCustomersToUnify(UNIFY_CUSTOMER_TYPE_PHONE)
      .then((dataResp) => {
        setContactList(dataResp.filter((objList) => objList.length > 0));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleConfirmSave = async () => {
    setIsSaving(true);
    try {
      const preparedCustomerIds = selectedIndexes.map((customerList) => customerList.map((c) => c.id));
      await CustomerService.unifyCustomers(preparedCustomerIds);
      showNotification('success', `Contatos unificados com sucesso.`);
      navigate('/clientes');
    } catch (e) {
      setIsModalConfirmOpen(false);
    } finally {
      setIsSaving(false);
    }
  };

  const isWorking = isSaving || isLoading;

  return (
    <div className={classes.root}>
      {/* CONFIRM */}
      <ConfirmationDialog
        title="Confirmação"
        message={
          <Alert severity="warning">
            Tem certeza que deseja unificar {selectedIndexes.length} contato(s)?
            <br />
            <b>Deseja prosseguir?</b>
          </Alert>
        }
        open={isModalConfirmOpen}
        onConfirm={handleConfirmSave}
        onClose={() => {
          setIsModalConfirmOpen(false);
        }}
      />

      {/* CONFIRM */}

      <Box display="flex" direction="row" justify="center" alignItems="center">
        <IconButton onClick={() => navigate(-1)} className={classes.btnBack} size="large">
          <FontAwesomeIconStatus icon="arrow-left" status="primary" />
        </IconButton>
        <Typography variant="h5">Unificação Automática</Typography>
      </Box>
      <Paper className={classes.paper}>
        {isWorking && (
          <div className={classes.paperSaving}>
            {isSaving && (
              <Typography variant="subtitle1" color="textSecondary">
                Unificando cadastros, por favor aguarde...
              </Typography>
            )}
            {isLoading && (
              <Typography variant="subtitle1" color="textSecondary">
                Por favor aguarde, carregando...
              </Typography>
            )}
            {isWorking && <LinearProgress />}
          </div>
        )}

        {!isWorking && (
          <>
            <UnifyCustomerAutomaticToolbar
              selectedIndexes={selectedIndexes}
              contactListLength={contactList.length}
              onSave={() => {
                setIsModalConfirmOpen(true);
              }}
            />

            <Table aria-label="simple table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="none" className={classes.cellCheckbox}>
                    {contactList.length > 0 && (
                      <Checkbox
                        indeterminate={
                          selectedIndexes?.length > 0 && selectedIndexes?.length < contactList?.length
                        }
                        checked={
                          selectedIndexes?.length > 0 && selectedIndexes?.length === contactList?.length
                        }
                        onChange={(e) => {
                          if (allBeingSelected) {
                            setSelectedIndexes([]);
                          } else {
                            setSelectedIndexes(contactList);
                          }
                          setAllBeingSelected((prevState) => !prevState);
                        }}
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Telefone 1</TableCell>
                  <TableCell>Telefone 2</TableCell>
                  <TableCell>Ocorrências</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {contactList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Alert className={classes.labelNoResults} severity="warning">
                        Nenhum resultado possível encontrado
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
                {contactList?.map((contact) => {
                  return (
                    <TableRow key={contact[0].id}>
                      <TableCell padding="none">
                        <Checkbox
                          color="primary"
                          checked={selectedIndexes.includes(contact)}
                          onChange={() => {
                            handleSelect(contact);
                          }}
                        />
                      </TableCell>
                      <TableCell padding="none">{contact[0].name}</TableCell>
                      <TableCell padding="none">{maskUtils.phone(contact[0].phone1)}</TableCell>
                      <TableCell padding="none">{maskUtils.phone(contact[0].phone2)}</TableCell>
                      <TableCell padding="none">{contact.map((o) => o.name).join(', ')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>
    </div>
  );
}

export default UnifyCustomerAutomaticPage;
